import { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { BrandGlyph, MODULE_COLOR } from '../lib/branding';
import { safeTopPad, safeBottomPad } from '../lib/safeArea';

const LAST_UPDATED = '2026-05-18';

interface LegalProps {
  title: string;
  children: ReactNode;
}

function LegalShell({ title, children }: LegalProps) {
  return (
    <main style={shellStyle}>
      <nav style={navStyle}>
        <Link to="/" style={navBrandLinkStyle}>
          <BrandGlyph size={24} color={MODULE_COLOR.angle} />
          <span style={{ fontSize: 14, fontWeight: 600 }}>CADtomie Intraop</span>
        </Link>
        <Link to="/" style={navBackStyle}>← Back</Link>
      </nav>

      <article style={articleStyle}>
        <h1 style={titleStyle}>{title}</h1>
        {children}
      </article>

      <footer style={footerStyle}>
        <Link to="/privacy" style={fLinkStyle}>Privacy</Link>
        <Link to="/terms" style={fLinkStyle}>Terms</Link>
        <Link to="/impressum" style={fLinkStyle}>Impressum</Link>
      </footer>
    </main>
  );
}

/* ──────────────── PRIVACY ──────────────── */

export function PrivacyPage() {
  return (
    <LegalShell title="Privacy Policy">
      <p style={p}>
        <i>Last updated: {LAST_UPDATED}.</i>
      </p>

      <h2 style={h2}>Data controller</h2>
      <p style={p}>
        Dr. med. Martin Nauroz, contact details under the{' '}
        <Link to="/impressum" style={a}>Impressum</Link>. Questions about data
        handling: <a href="mailto:cadtomie@gmail.com" style={a}>cadtomie@gmail.com</a>.
      </p>

      <h2 style={h2}>Short version</h2>
      <p style={p}>
        CADtomie Intraop processes camera frames, gyroscope readings, and your
        measurement annotations <b>locally on your device</b>. None of this
        measurement content is uploaded to CADtomie servers. The account data
        used for sign-in is limited to your email address, a Supabase user
        identifier, and session tokens issued by Supabase.
      </p>

      <h2 style={h2}>What we collect</h2>
      <p style={p}>
        <b>Account data used for sign-in:</b><br />
        email address · Supabase user identifier · session token issued when
        you sign in. Passwords are processed by Supabase authentication and
        are never stored by CADtomie in plain text.
      </p>
      <p style={p}>
        <b>Stored on your device only, not uploaded to CADtomie:</b><br />
        camera frames captured in the measurement modules · gyroscope and
        accelerometer readings · two-line angles, distances, and torsion deltas
        you compute · annotations you draw · images you save with the Save
        action (kept in the device's local storage / IndexedDB) · the active
        session token (kept in the WebView's localStorage on iOS, in a
        cookie scoped to <code>.cadtomie.com</code> on the web).
      </p>
      <p style={p}>
        <b>Not collected at all:</b><br />
        patient names · date of birth · medical record numbers · any DICOM
        data · location (we never request location permission) · contacts ·
        device identifiers for advertising · analytics events · crash reports.
      </p>

      <h2 style={h2}>What happens when you export an image</h2>
      <p style={p}>
        The Export action hands the annotated image to your operating system's
        share sheet. From that point on, what happens to the image is governed
        by whichever destination you pick (Photos, Mail, AirDrop, Files, etc.).
        We do not see the image and have no record of where it went.
      </p>

      <h2 style={h2}>Sub-processors</h2>
      <p style={p}>
        We use a small number of service providers to make CADtomie Intraop
        run. Each receives only the data described above.
      </p>
      <p style={p}>
        <b>Supabase Inc. (USA)</b> — handles authentication (sign-in,
        password reset) and stores your account record. EU users' data may be
        transferred to the United States; transfers rely on the EU Standard
        Contractual Clauses. See{' '}
        <a href="https://supabase.com/privacy" style={a}>supabase.com/privacy</a>.
      </p>
      <p style={p}>
        <b>Cloudflare, Inc. (global edge network)</b> — serves the web PWA
        version of the app from edge nodes near you. Observes standard request
        metadata (IP address, user-agent, path, timestamp) for delivery and
        abuse prevention. See{' '}
        <a href="https://www.cloudflare.com/privacypolicy/" style={a}>
          cloudflare.com/privacypolicy
        </a>.
      </p>
      <p style={p}>
        <b>Apple, Inc.</b> — distributes the iOS app via the App Store and
        TestFlight. Apple may collect crash diagnostics and usage statistics
        you have opted into in iOS Settings → Privacy & Security → Analytics
        & Improvements. We do not receive these.
      </p>

      <h2 style={h2}>Sensors and camera permissions</h2>
      <p style={p}>
        Each measurement module requests access to the device camera and
        motion sensors only when you open it. iOS displays the standard
        system permission prompts using the reasons listed in our Info.plist:
        "to capture reference photos" (camera) and "to compute angles relative
        to gravity" (motion). You can revoke
        either permission at any time in iOS Settings → CADtomie Intraop.
      </p>

      <h2 style={h2}>How long we keep your data</h2>
      <p style={p}>
        Your account record (email, hashed password, Supabase user ID) is
        kept for as long as your account exists. Session tokens are kept on
        your device for up to 30 days from your last sign-in and renew
        automatically while you continue to use the app.
      </p>

      <h2 style={h2}>Deleting your account</h2>
      <p style={p}>
        You can delete your CADtomie account at any time directly from inside
        the signed-in app: open{' '}
        <b>Account → Danger zone → Delete account</b>. Your email address and
        Supabase user record are removed immediately. If you prefer, you can
        also email{' '}
        <a href="mailto:cadtomie@gmail.com?subject=Account%20deletion%20request" style={a}>
          cadtomie@gmail.com
        </a>{' '}
        from the address tied to your account. Locally stored content
        (annotated images, captured frames) lives only on your device and is
        removed when you uninstall the app or clear the app's data. Active
        App Store subscriptions are not cancelled by account deletion — cancel
        them separately under iOS Settings → Apple ID → Subscriptions.
      </p>

      <h2 style={h2}>Your rights under GDPR / DSGVO</h2>
      <p style={p}>
        You have the right to access (Art. 15), rectify (Art. 16), erase
        (Art. 17), restrict processing of (Art. 18), and port (Art. 20) your
        personal data, and to object (Art. 21) or withdraw consent at any
        time. Exercise these by emailing{' '}
        <a href="mailto:cadtomie@gmail.com" style={a}>cadtomie@gmail.com</a>.
        You also have the right to lodge a complaint with a supervisory
        authority — in Germany, this is the data-protection authority of the
        federal state in which you reside.
      </p>

      <h2 style={h2}>Cookies and tracking</h2>
      <p style={p}>
        We do not set tracking cookies. We do not use third-party analytics
        that fingerprint or identify visitors. The only browser storage we
        use is technically necessary for signing in (session cookie or
        localStorage) and for the local image gallery you create (IndexedDB).
      </p>

      <h2 style={h2}>Children</h2>
      <p style={p}>
        CADtomie Intraop is a professional tool for clinicians. It is not
        directed at children under 16 and we do not knowingly collect data
        from them.
      </p>

      <h2 style={h2}>Changes to this policy</h2>
      <p style={p}>
        If we change how the app handles data, we will update this page and
        bump the "Last updated" date at the top. Substantive changes will
        also be reflected in the app's release notes.
      </p>
    </LegalShell>
  );
}

/* ──────────────── TERMS ──────────────── */

export function TermsPage() {
  return (
    <LegalShell title="Terms of Use">
      <p style={p}>
        <i>Last updated: {LAST_UPDATED}.</i>
      </p>

      <h2 style={h2}>Nature of the tool</h2>
      <p style={p}>
        CADtomie Intraop is an educational and measurement assistance utility
        for orthopaedic professionals and trainees. It is provided "as is",
        without warranty of any kind. It is <b>not a medical device</b>, has
        not received CE certification or FDA clearance, and is not intended to
        diagnose, treat, monitor, or replace established imaging, navigation,
        or clinical judgement.
      </p>

      <h2 style={h2}>Intended use</h2>
      <p style={p}>
        The tool is offered as a personal, professional learning and
        documentation aid for cross-checking measurements. It must not be used
        as the sole basis for patient care. Any measurement should be checked
        against an established method such as fluoroscopy, navigation, manual
        goniometer, or PACS measurement.
      </p>

      <h2 style={h2}>Subscriptions</h2>
      <p style={p}>
        CADtomie Intraop Pro is offered as an auto-renewing subscription
        through the App Store. Payment is charged to your Apple ID at
        confirmation of purchase. Your subscription automatically renews
        unless auto-renew is turned off at least 24 hours before the end of
        the current period. Your Apple ID will be charged for renewal within
        24 hours prior to the end of the current period. You can manage your
        subscription and turn off auto-renewal in your Apple ID account
        settings (iOS Settings → Apple ID → Subscriptions). No refund is
        available for the unused portion of any term, except where required
        by applicable law.
      </p>

      <h2 style={h2}>Educational (free) access</h2>
      <p style={p}>
        A free educational tier remains available for non-commercial learning
        and demonstration. It includes the core viewer experience without the
        full intraoperative measurement workflow.
      </p>

      <h2 style={h2}>No warranty</h2>
      <p style={p}>
        The author makes no warranty, express or implied, regarding accuracy,
        fitness for a particular purpose, reliability under specific OR
        conditions, or continued availability. Use at your own risk.
      </p>

      <h2 style={h2}>Liability</h2>
      <p style={p}>
        To the maximum extent permitted by applicable law, the author
        disclaims any liability for direct, indirect, incidental, special, or
        consequential damages arising from the use of, or inability to use,
        this tool — including, without limitation, any clinical outcome.
      </p>

      <h2 style={h2}>Intellectual property</h2>
      <p style={p}>
        The CADtomie name, glyph, and the application's source code are the
        property of the author. The application may not be redistributed or
        rebranded without permission.
      </p>

      <h2 style={h2}>Changes</h2>
      <p style={p}>
        These terms may be updated as the tool evolves. Substantive changes
        will be reflected in the version stamp on the application.
      </p>
    </LegalShell>
  );
}

/* ──────────────── IMPRESSUM ──────────────── */

export function ImpressumPage() {
  return (
    <LegalShell title="Impressum">
      <p style={p}>
        <i>
          Last updated: {LAST_UPDATED}.
        </i>
      </p>

      <h2 style={h2}>Anbieter</h2>
      <p style={p}>
        Dr. med. Martin Nauroz<br />
        Gropiusring 69<br />
        22309 Hamburg<br />
        Deutschland
      </p>

      <h2 style={h2}>Kontakt</h2>
      <p style={p}>
        E-Mail: <a href="mailto:cadtomie@gmail.com" style={a}>cadtomie@gmail.com</a>
      </p>

      <h2 style={h2}>Verantwortlich für den Inhalt nach §18 Abs. 2 MStV</h2>
      <p style={p}>
        Dr. med. Martin Nauroz<br />
        Gropiusring 69<br />
        22309 Hamburg<br />
        Deutschland
      </p>

      <h2 style={h2}>Haftungsausschluss</h2>
      <p style={p}>
        CADtomie Intraop ist ein Forschungs- und Assistenzwerkzeug. Es ist kein
        zugelassenes Medizinprodukt im Sinne der MDR (EU 2017/745) und nicht
        zur Diagnose, Therapie, Überwachung oder als Ersatz für etablierte
        bildgebende oder navigationsgestützte Verfahren bestimmt. Messergebnisse
        müssen mit etablierten Methoden überprüft werden.
      </p>

      <h2 style={h2}>Streitschlichtung</h2>
      <p style={p}>
        Die Europäische Kommission stellt eine Plattform zur
        Online-Streitbeilegung (OS) bereit:{' '}
        <a href="https://ec.europa.eu/consumers/odr" style={a}>
          https://ec.europa.eu/consumers/odr
        </a>
        . Wir sind nicht bereit oder verpflichtet, an
        Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle
        teilzunehmen.
      </p>
    </LegalShell>
  );
}

/* ──────────────── ACCOUNT DELETION ──────────────── */

export function DeleteAccountPage() {
  return (
    <LegalShell title="Delete your CADtomie account">
      <p style={p}>
        <i>Last updated: {LAST_UPDATED}.</i>
      </p>

      <p style={p}>
        This page exists so that any user — whether or not they currently
        have the CADtomie Intraop app installed — can request deletion of
        their CADtomie account and any data tied to it.
      </p>

      <h2 style={h2}>Option 1 — In the app (fastest)</h2>
      <p style={p}>
        Open the CADtomie Intraop app, sign in, and tap your avatar /
        e-mail address at the top right to open <b>Account Settings</b>.
        Scroll to the <b>Danger Zone</b> at the bottom and tap{' '}
        <b>Delete account</b>. Confirm the prompt. Your sign-in record is
        removed immediately and you are signed out automatically. No
        waiting period.
      </p>

      <h2 style={h2}>Option 2 — By e-mail (if you no longer have the app)</h2>
      <p style={p}>
        Send an e-mail to{' '}
        <a href="mailto:cadtomie@gmail.com?subject=CADtomie%20account%20deletion%20request" style={a}>
          cadtomie@gmail.com
        </a>{' '}
        from the e-mail address tied to the account you want to delete.
        Subject: <i>"CADtomie account deletion request"</i>. Include the
        e-mail address of the account so we can locate it. We action all
        deletion requests within 14 days and confirm completion in writing.
      </p>

      <h2 style={h2}>What is deleted</h2>
      <p style={p}>
        <b>Removed from our servers:</b>
        <br />
        • Your e-mail address
        <br />
        • The Supabase user record (UUID, hashed password, sign-in
        timestamps)
        <br />
        • Any RevenueCat subscription history tied to the account
      </p>
      <p style={p}>
        <b>Not stored on our servers — but lives on your device:</b>
        <br />
        Locally captured content (annotated images, captured frames,
        cached preferences) is stored only inside the CADtomie Intraop app
        on your phone. Uninstalling the app removes that content. We never
        receive a copy.
      </p>

      <h2 style={h2}>Active App Store / Play Store subscriptions</h2>
      <p style={p}>
        Deleting your CADtomie account does <b>not</b> automatically
        cancel an active App Store or Google Play subscription —
        subscriptions are managed by Apple or Google, not by us. To cancel
        a subscription you must do so separately in:
        <br />
        • iOS: <i>Settings → Apple ID → Subscriptions</i>
        <br />
        • Android: <i>Play Store app → Profile → Payments &amp;
        subscriptions → Subscriptions</i>
      </p>

      <h2 style={h2}>Retention after deletion</h2>
      <p style={p}>
        Once your account is deleted, your e-mail address and user record
        are removed from the live database immediately. Encrypted database
        backups may retain the record for up to 30 days, after which the
        backup is overwritten. Beyond that window, no copy of your account
        data exists on our infrastructure.
      </p>

      <h2 style={h2}>Questions or problems</h2>
      <p style={p}>
        If you don't receive a confirmation after submitting a deletion
        request, or if the in-app flow fails, contact{' '}
        <a href="mailto:cadtomie@gmail.com" style={a}>
          cadtomie@gmail.com
        </a>{' '}
        and we will resolve it manually.
      </p>
    </LegalShell>
  );
}

/* ════════════════ Shared styles ════════════════ */

const shellStyle: CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100dvh',
  background: '#000',
};
const navStyle: CSSProperties = {
  maxWidth: 760,
  width: '100%',
  margin: '0 auto',
  paddingTop: safeTopPad(18),
  paddingBottom: 18,
  paddingLeft: 22,
  paddingRight: 22,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};
const navBrandLinkStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 10,
  color: 'var(--text)',
  textDecoration: 'none',
};
const navBackStyle: CSSProperties = {
  fontSize: 14,
  color: 'var(--text-dim)',
  textDecoration: 'none',
};
const articleStyle: CSSProperties = {
  maxWidth: 760,
  width: '100%',
  margin: '0 auto',
  padding: '20px 22px 80px',
  flex: 1,
};
const titleStyle: CSSProperties = {
  margin: '8px 0 28px',
  fontSize: 36,
  fontWeight: 700,
  letterSpacing: -0.8,
  color: 'var(--text)',
};
const h2: CSSProperties = {
  fontSize: 18,
  fontWeight: 600,
  marginTop: 28,
  marginBottom: 8,
  color: 'var(--text)',
};
const p: CSSProperties = {
  margin: '0 0 12px',
  color: 'var(--text-dim)',
  fontSize: 15,
  lineHeight: 1.6,
};
const a: CSSProperties = { color: "#95a2c8", textDecoration: 'none' };
const footerStyle: CSSProperties = {
  maxWidth: 760,
  width: '100%',
  margin: '0 auto',
  paddingTop: 24,
  paddingBottom: safeBottomPad(32),
  paddingLeft: 22,
  paddingRight: 22,
  borderTop: '1px solid rgba(255,255,255,0.08)',
  display: 'flex',
  gap: 16,
};
const fLinkStyle: CSSProperties = {
  fontSize: 13,
  color: 'var(--text-dim)',
  textDecoration: 'none',
};
